import { useEffect, useMemo, useState } from "react";
import { PawPrint, FileDown } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";
import { FreebieCard } from "@/components/freebies/FreebieCard";
import { ComingSoonModal } from "@/components/ComingSoonModal";
import { NAV_ROOMS } from "@/data/doorways";
import { api } from "@/lib/api";

const CATEGORIES = [
  { id: "stationery", label: "Stationery", thumb: "/images/freebies/tab-stationery.png" },
  { id: "scrapbook_junk_journal", label: "Scrapbook & Junk Journal", thumb: "/images/freebies/tab-scrapbook.png" },
  { id: "coloring_pages", label: "Coloring Pages", thumb: "/images/freebies/tab-coloring.png" },
];

const EMPTY_COPY = {
  stationery: "New paper goods are being folded up — check back soon.",
  scrapbook_junk_journal: "Fresh journal treasures are being gathered — check back soon.",
  coloring_pages: "Fresh coloring pages are being drawn up — check back soon.",
};

function getVisitorId() {
  let v = localStorage.getItem("mdd_visitor_id");
  if (!v) {
    v = crypto.randomUUID();
    localStorage.setItem("mdd_visitor_id", v);
  }
  return v;
}

export default function Freebies() {
  const [category, setCategory] = useState("stationery");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hearted, setHearted] = useState(() => new Set());
  const [navRoom, setNavRoom] = useState(null);
  const visitorId = useMemo(getVisitorId, []);

  const subscribeNav = async (email) => {
    const { data } = await api.post("/subscribe", {
      email,
      interest: navRoom.name,
      source: navRoom.source,
    });
    return data;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/freebies", { params: { category } })
      .then(({ data }) => {
        if (!cancelled) {
          setItems(data.items);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  useEffect(() => {
    api
      .get("/freebies/hearted", { params: { visitor_id: visitorId } })
      .then(({ data }) => setHearted(new Set(data.ids)))
      .catch(() => {});
  }, [visitorId]);

  const toggleHeart = async (id) => {
    const was = hearted.has(id);
    const apply = (h, count) => {
      setHearted((prev) => {
        const n = new Set(prev);
        h ? n.add(id) : n.delete(id);
        return n;
      });
      if (count !== undefined)
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, heart_count: count } : it)));
    };
    apply(!was);
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, heart_count: it.heart_count + (was ? -1 : 1) } : it))
    );
    try {
      const { data } = await api.post(`/freebies/${id}/heart`, { visitor_id: visitorId });
      apply(data.hearted, data.heart_count);
    } catch {
      apply(was);
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, heart_count: it.heart_count + (was ? 1 : -1) } : it))
      );
    }
  };

  const download = (item) => {
    window.location.assign(
      `${process.env.REACT_APP_BACKEND_URL}/api/freebies/${item.id}/download`
    );
    setItems((prev) =>
      prev.map((it) => (it.id === item.id ? { ...it, download_count: it.download_count + 1 } : it))
    );
  };

  return (
    <div className="min-h-screen overflow-x-clip" data-testid="freebies-page">
      <Header onOpenNavRoom={(key) => setNavRoom(NAV_ROOMS[key])} />

      <main className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-10">
        {/* Guide callout */}
        <div
          data-testid="guide-callout"
          className="mx-auto mb-4 flex max-w-3xl flex-col items-center gap-3 rounded-[24px] bg-[#A8BFCE]/35 px-6 py-6 text-center sm:mb-5 sm:flex-row sm:gap-5 sm:px-8 sm:text-left"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/70">
            <FileDown className="h-5 w-5 text-[#6E8CA3]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink">
              How to use your freebie
            </p>
            <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">
              Download our quick guide for adding your PNG to Word, positioning it, and
              printing on the paper you choose.
            </p>
          </div>
          <a
            href="/downloads/My_Dachshund_Days_How_to_Use_Freebies.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-guide-button"
            className="inline-flex min-h-[48px] shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#6E8CA3] px-6 text-[12px] font-semibold uppercase tracking-[0.14em] text-ivory transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            Download the Guide
          </a>
        </div>

        {/* Hero banner */}
        <div
          data-testid="freebies-hero"
          className="relative overflow-hidden rounded-[26px] aspect-[3.3/1] sm:aspect-[16/5] lg:aspect-auto lg:h-[200px] xl:h-[220px]"
        >
          <img
            src="/images/freebies/banner.jpg"
            srcSet="/images/freebies/banner-sm.jpg 760w, /images/freebies/banner.jpg 1609w"
            sizes="(max-width: 1024px) 100vw, 1360px"
            alt="A long-haired dachshund resting on a cozy blanket beside books, botanical prints and daisies"
            className="hidden h-full w-full object-cover sm:block"
            style={{ objectPosition: "72% center" }}
          />
          <img
            src="/images/freebies/banner-mobile.jpg"
            alt="A long-haired dachshund resting on a cozy blue blanket beside daisies"
            className="h-full w-full object-cover sm:hidden"
          />
          <div className="absolute inset-y-0 left-0 flex w-[62%] flex-col justify-center pl-[7%] sm:w-1/2">
            <PawPrint
              aria-hidden
              className="h-4 w-4 text-[#6E8CA3] sm:h-5 sm:w-5"
              fill="#6E8CA3"
              strokeWidth={0}
              style={{ filter: "drop-shadow(0 0 6px rgba(255,250,240,1)) drop-shadow(0 0 14px rgba(255,250,240,0.9))" }}
            />
            <h1 className="text-protect mt-1.5 font-serif text-[34px] font-medium leading-none tracking-tight text-ink sm:mt-2 sm:text-5xl lg:text-6xl">
              Freebies
            </h1>
            <p className="text-protect mt-2.5 max-w-[420px] text-[8.5px] font-semibold uppercase leading-[1.9] tracking-[0.2em] text-ink sm:mt-3 sm:text-[11px]">
              Printables, wall art, planners & cute paper goods
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div className="mx-auto mt-5 grid max-w-3xl grid-cols-3 gap-2 sm:mt-7 sm:gap-4" role="tablist" aria-label="Freebie categories">
          {CATEGORIES.map((cat) => {
            const active = category === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`tab-${cat.id}`}
                onClick={() => setCategory(cat.id)}
                className={`flex min-h-[56px] items-center justify-center gap-2 rounded-2xl px-2 py-2 transition-colors duration-300 sm:min-h-[72px] sm:gap-3 sm:px-4 ${
                  active ? "bg-[#A8BFCE]" : "bg-white/70 hover:bg-white"
                }`}
              >
                <img src={cat.thumb} alt="" aria-hidden className="h-7 w-auto rounded-md sm:h-10" />
                <span className="font-serif text-[13px] font-medium leading-tight text-ink sm:text-lg">
                  {cat.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {loading ? (
          <div data-testid="freebies-loading" className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 sm:mt-9 lg:grid-cols-4 lg:gap-x-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[2/1] w-full rounded-2xl bg-ink/[0.06]" />
                <div className="mt-3 h-4 w-3/4 rounded bg-ink/[0.06]" />
                <div className="mt-2 h-3 w-1/2 rounded bg-ink/[0.06]" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="freebies-empty" className="flex flex-col items-center py-20 text-center">
            <PawPrint className="h-8 w-8 text-[#6E8CA3]" fill="#6E8CA3" strokeWidth={0} />
            <p className="mt-4 font-serif text-xl text-ink">{EMPTY_COPY[category]}</p>
            <p className="mt-2 text-sm text-ink/55">Bobby is preparing something lovely for this shelf.</p>
          </div>
        ) : (
          <div data-testid="freebies-grid" className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 sm:mt-9 lg:grid-cols-4 lg:gap-x-6">
            {items.map((item, i) => (
              <FreebieCard
                key={item.id}
                item={item}
                index={i}
                hearted={hearted.has(item.id)}
                onToggleHeart={toggleHeart}
                onDownload={download}
              />
            ))}
          </div>
        )}
      </main>

      <Marquee />
      <Footer />

      <ComingSoonModal
        room={navRoom}
        open={Boolean(navRoom)}
        onClose={() => setNavRoom(null)}
        onSubscribe={subscribeNav}
      />
    </div>
  );
}
